import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { Locale } from "@/lib/i18n/config";
import type { SellerCustomerView } from "@/lib/api/types";
import { initialsOf, tintFor } from "@/modules/customers/lib/customer-avatar";

type CustomersTableProps = {
  locale: Locale;
  items: SellerCustomerView[];
  selectedId?: string | null;
  onSelect: (customer: SellerCustomerView) => void;
  /** "row" = compact rows (desktop split pane); "card" = big cards (phone/tablet). */
  variant?: "row" | "card";
};

/**
 * Customers list. Two renderings share the same data (name / governorate /
 * order count / total spend): compact **rows** for the desktop split pane, and
 * big **cards** (matching the design's mobile layout) for phone + tablet.
 */
export function CustomersTable({
  locale,
  items,
  selectedId,
  onSelect,
  variant = "row",
}: CustomersTableProps) {
  const t = useTranslations();
  const numberFmt = new Intl.NumberFormat(locale === "ar" ? "ar-EG" : "en-US");

  const describe = (customer: SellerCustomerView) => {
    const governorateName = customer.governorate
      ? locale === "ar"
        ? customer.governorate.nameAr
        : (customer.governorate.nameEn ?? customer.governorate.nameAr)
      : null;
    const subline = [
      governorateName,
      t(keys.customers.ordersCountLabel, { count: customer.ordersCount }),
    ]
      .filter(Boolean)
      .join(" · ");
    return { subline, tint: tintFor(customer.name || "?") };
  };

  if (variant === "card") {
    return (
      <ul className="flex flex-col gap-[11px]">
        <li className="sr-only">{t(keys.customers.tableName)}</li>
        {items.map((customer) => {
          const { subline, tint } = describe(customer);
          return (
            <li key={customer.id}>
              <button
                type="button"
                onClick={() => onSelect(customer)}
                aria-label={customer.name}
                className="flex w-full items-center gap-3 rounded-[16px] border border-[color:var(--card-br)] bg-[color:var(--surface)] p-3 text-start transition-colors active:bg-[color:var(--surface-alt)]"
              >
                <span
                  aria-hidden="true"
                  className="flex h-[50px] w-[50px] shrink-0 items-center justify-center rounded-full text-[15px] font-bold"
                  style={{ background: tint.bg, color: tint.fg }}
                >
                  {initialsOf(customer.name || "?")}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[14.5px] font-semibold leading-tight text-[color:var(--text-primary)]">
                    {customer.name}
                  </span>
                  <span className="mt-[3px] block truncate text-[11.5px] text-[color:var(--text-muted)]">
                    {subline}
                  </span>
                  <span className="mt-1.5 flex items-baseline gap-[5px]">
                    <span className="text-[10.5px] font-semibold text-[color:var(--text-muted)]">
                      EGP
                    </span>
                    <span className="text-[15.5px] font-bold text-[color:var(--text-primary)]">
                      {numberFmt.format(customer.totalSpent)}
                    </span>
                    <span className="ms-0.5 text-[11px] text-[color:var(--text-muted)]">
                      {t(keys.customers.spentLabel)}
                    </span>
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <ul className="flex flex-col gap-0.5">
      <li className="sr-only">{t(keys.customers.tableName)}</li>
      {items.map((customer) => {
        const { subline, tint } = describe(customer);
        const isActive = selectedId === customer.id;
        return (
          <li key={customer.id}>
            <button
              type="button"
              onClick={() => onSelect(customer)}
              aria-current={isActive ? "true" : undefined}
              aria-label={customer.name}
              className={[
                "flex w-full items-center gap-[11px] rounded-xl px-[11px] py-2.5 text-start transition-colors",
                isActive
                  ? "bg-[color:var(--surface-alt)] shadow-[inset_0_0_0_1px_var(--border-strong)]"
                  : "hover:bg-[color:var(--surface-alt)]",
              ].join(" ")}
            >
              <span
                aria-hidden="true"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[13.5px] font-bold"
                style={{ background: tint.bg, color: tint.fg }}
              >
                {initialsOf(customer.name || "?")}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13.5px] font-semibold text-[color:var(--text-primary)]">
                  {customer.name}
                </span>
                <span className="block truncate text-[11.5px] text-[color:var(--text-muted)]">
                  {subline}
                </span>
              </span>
              <span className="shrink-0 whitespace-nowrap text-[12.5px] font-bold text-[color:var(--text-primary)]">
                {numberFmt.format(customer.totalSpent)}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
